// ── OddsBooksTable — every OddsPapi book for the selected line ──────
//
// Replaces the Unabated consensus chart in GameDetail (Dan, 2026-10-10).
// #1145 places it between Moneyline and Spread/Total. One row per side of
// the market, one column per book: the
// OddsPapi consensus first, then Pinnacle, then the field books. The table
// follows the line the trade panel is on (oddsBoard.resolveOddsBoardSelection)
// and shows the full-game moneyline when nothing else is selected — or,
// labelled, while the selected line has no OddsPapi quotes, so it is never
// empty on load.
//
// Freshness: a cell lights when its quote updates — green when the side's
// probability rose, red when it fell, grey when the book re-quoted the same
// price — and fades over the window that quote carries its fresh weight in
// the consensus math: 5 s for field books and the consensus itself
// (OP_FIELD_FRESH_MS), 6 s for Pinnacle (OP_ANCHOR_GLOW_MS). A cell that
// mounts on an already-aging quote (page open, line switch) shows only what
// is left of that window, in grey: there is no previous price to compare.
//
// Each book's header carries a last-update clock (quoteAgeLabel: 1s…5s, then
// 5s+, 10s+, 20s+, 30s+, 60s+) on the app's shared one-second clock. Pinnacle
// keeps its column even when it is not quoting.
//
// Nothing moves sideways (Dan, 2026-10-10): every column has a fixed width,
// and the field is capped at the ten most recently updated books, each
// keeping its slot (nextFieldBookSlots) — a newcomer takes over a quiet
// book's column in place.

/** Column widths, in rem: side label, consensus, each book. */
const LABEL_REM = 7;
const CONSENSUS_REM = 4.75;
const BOOK_REM = 4.5;

import { useMemo, useState } from 'react';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import {
  americanForQuote,
  decimalToImpliedPct,
  decimalToImpliedPctText,
  fairAmericanText,
  fairPctText,
  formatOpAge,
  OP_ANCHOR_GLOW_MS,
  OP_FIELD_FRESH_MS,
  opWithdrawReasonLabel,
} from '@/lib/opOdds';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useMarketStore } from '@/stores/marketStore';
import { GAME_DETAIL_TABLE_FADE } from './CompactLinesTable';
import {
  FIELD_BOOK_CAP,
  nextFieldBookSlots,
  oddsBoardSides,
  oddsBoardTitle,
  oddsBookLabel,
  quoteAgeLabel,
  resolveOddsBoardSlice,
  type OddsBoardSelection,
} from './oddsBoard';

type TickDir = 'up' | 'down' | 'neutral';
/** Literal names, so Tailwind's content scan keeps the @layer utilities
 *  rules (globals.css). */
const TICK_CLASS: Record<TickDir, string> = {
  up: 'animate-odds-tick-up',
  down: 'animate-odds-tick-down',
  neutral: 'animate-odds-tick-neutral',
};
interface Tick {
  value: number | null;
  stamp: number | null;
  dir: TickDir;
  /** Frozen per highlight: re-deriving it from the clock on every render
   *  would re-time the running animation. */
  delayMs: number;
}

/** The highlight a cell is running. An update seen while mounted starts a
 *  full window now (provider clocks can lead or lag ours); the quote a cell
 *  mounts with starts part-way, by its own age. Derived during render, the
 *  same pattern as useChangeKey. */
function useTick(value: number | null, stamp: number | null, windowMs: number): Tick {
  const [tick, setTick] = useState<Tick>(() => ({
    value,
    stamp,
    dir: 'neutral',
    delayMs: stamp == null ? 0 : -Math.min(Math.max(0, Date.now() - stamp), windowMs),
  }));
  if (tick.stamp !== stamp) {
    const dir: TickDir =
      value == null || tick.value == null || value === tick.value ? 'neutral' : value > tick.value ? 'up' : 'down';
    const next = { value, stamp, dir, delayMs: 0 };
    setTick(next);
    return next;
  }
  return tick;
}

function ageText(ms: number, now: number): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  return s < 60 ? `${s}s ago` : formatOpAge(ms, now);
}

function TickCell({
  value,
  stamp,
  windowMs,
  text,
  sub,
  title,
  emphasis = false,
}: {
  /** What the direction compares: the side's probability. */
  value: number | null;
  stamp: number | null;
  windowMs: number;
  text: string;
  sub: string | null;
  title?: string;
  emphasis?: boolean;
}) {
  const tick = useTick(value, stamp, windowMs);
  return (
    <td className="px-0.5 py-0.5 text-center" title={title}>
      <span
        // Remount per quote so the one-shot animation restarts.
        key={stamp ?? 'none'}
        data-tick={stamp != null ? tick.dir : undefined}
        className={cn(
          'inline-block min-w-[3.25rem] rounded px-1 py-0.5 tabular-nums',
          stamp != null && TICK_CLASS[tick.dir],
        )}
        style={stamp != null ? { animationDuration: `${windowMs}ms`, animationDelay: `${tick.delayMs}ms` } : undefined}
      >
        <span className={cn('block text-xs', emphasis ? 'font-bold' : 'font-semibold')}>{text}</span>
        {sub != null && <span className="text-muted-foreground block text-[10px] leading-tight">{sub}</span>}
      </span>
    </td>
  );
}

interface Props {
  gameKey: string | null;
  awayCode: string;
  homeCode: string;
  selection: OddsBoardSelection;
}

export function OddsBooksTable({ gameKey, awayCode, homeCode, selection }: Props) {
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const version = useMarketStore((s) => s.version);
  const selectionKey = JSON.stringify(selection);
  // The selected line's slice; while that line has no OddsPapi quotes (or
  // no OddsPapi market at all), the full-game moneyline instead, labelled as
  // such. The page always has the moneyline's consensus and Pinnacle — its
  // Moneyline row shows them — so the table never sits empty waiting on a
  // line OddsPapi may not quote. Never a neighbouring line: a nearby strike
  // is a different contract.
  const { slice, fallback } = useMemo(() => {
    void version;
    if (!gameKey || !showOpOdds) return { slice: null, fallback: false };
    const st = useMarketStore.getState();
    const chosen = JSON.parse(selectionKey) as OddsBoardSelection;
    const selected = resolveOddsBoardSlice(chosen, { key: gameKey, homeCode }, {
      moneyline: st.getOpOddsFor,
      spread: st.getOpSpreadLineFor,
      total: st.getOpTotalLineFor,
      bucket: st.getOpMarketFor,
    });
    if (selected || chosen.kind === 'moneyline') return { slice: selected, fallback: false };
    const moneyline = st.getOpOddsFor(gameKey);
    return { slice: moneyline, fallback: moneyline != null };
  }, [version, gameKey, homeCode, selectionKey, showOpOdds]);

  const title = oddsBoardTitle(fallback ? { kind: 'moneyline' } : selection, { awayCode }, slice);
  const fallbackNote = !fallback ? null : selection.kind === 'unmapped'
    ? 'the selected line has no OddsPapi market — showing the moneyline'
    : `${oddsBoardTitle(selection, { awayCode }, null)} has no OddsPapi quotes yet — showing the moneyline`;
  // Tooltip ages only; the highlights run on CSS time.
  const now = useSharedNow(1000);
  const sides = slice ? oddsBoardSides(slice, { awayCode, homeCode }) : [];
  const consensus = slice?.market.consensus ?? null;
  const withdrawn = new Map((slice?.market.withdrawnBooks ?? []).map((w) => [w.bookmaker, w]));
  const updatedAt = new Map((slice?.market.books ?? []).map((b) => [b.bookmaker, b.updatedAtMs]));
  // Books actually quoting — Pinnacle's column stands even when it is not.
  const quoting = slice?.market.books.length ?? 0;
  // Every cell remounts when the market changes, so a line switch never
  // reads as a tick.
  const marketId = slice
    ? [gameKey, slice.market.marketType, slice.market.period ?? slice.market.periodTypeId, slice.market.points,
      slice.market.stat, slice.market.playerKey].join('|')
    : selectionKey;

  // The field-book slots carry over render to render within one market, so a
  // book keeps its column; a new market lays out fresh. Derived during
  // render (nextFieldBookSlots is idempotent on its own output).
  const [layout, setLayout] = useState<{ id: string; slots: readonly string[] }>({ id: '', slots: [] });
  const fieldBooks = slice ? nextFieldBookSlots(layout.id === marketId ? layout.slots : [], slice.market, now) : [];
  if (slice && (layout.id !== marketId || fieldBooks.join() !== layout.slots.join())) {
    setLayout({ id: marketId, slots: fieldBooks });
  }
  const books = ['pinnacle', ...fieldBooks];
  // Field books the cap leaves out (quoting or withdrawn, Pinnacle aside).
  const hidden = new Set([...(slice?.market.books ?? []), ...(slice?.market.withdrawnBooks ?? [])]
    .map((b) => b.bookmaker).filter((b) => b !== 'pinnacle' && !fieldBooks.includes(b))).size;

  let empty: string | null = null;
  if (!showOpOdds) empty = 'OddsPapi is hidden by the odds filter.';
  else if (!slice || (quoting === 0 && withdrawn.size === 0 && consensus == null)) {
    empty = selection.kind === 'unmapped' ? 'OddsPapi has no market for the selected line.' : 'No OddsPapi quotes for this market yet.';
  }

  return (
    <section data-testid="odds-books-table">
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="text-foreground text-sm font-semibold">OddsPapi</span>
          <span className="text-muted-foreground shrink-0 text-xs" data-testid="odds-books-title">{title}</span>
          {fallbackNote && (
            <span className="truncate text-[10px] text-amber-300/80" data-testid="odds-books-fallback" title={fallbackNote}>
              {fallbackNote}
            </span>
          )}
        </div>
        {!empty && (
          <span className="text-muted-foreground shrink-0 text-[10px]">
            {quoting} {quoting === 1 ? 'book' : 'books'}
            {hidden > 0 && ` · ${FIELD_BOOK_CAP} most recent shown`}
            {consensus != null && ` · consensus of ${consensus.contributingBooks}`}
          </span>
        )}
      </div>
      {empty ? (
        <div className="text-muted-foreground px-3 py-4 text-center text-xs">{empty}</div>
      ) : (
        <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
          <table
            className="table-fixed border-collapse border-spacing-0"
            style={{ width: `${LABEL_REM + CONSENSUS_REM + books.length * BOOK_REM}rem` }}
          >
            <colgroup>
              <col style={{ width: `${LABEL_REM}rem` }} />
              <col style={{ width: `${CONSENSUS_REM}rem` }} />
              {books.map((b) => <col key={b} style={{ width: `${BOOK_REM}rem` }} />)}
            </colgroup>
            <thead className="bg-muted/50 border-border border-b">
              <tr className="text-muted-foreground text-[10px] font-semibold uppercase">
                <th className="px-2 py-1 text-left" />
                <th
                  className="text-foreground px-1 py-1 text-center"
                  title="OddsPapi consensus — the de-vigged fair value across the contributing sportsbooks, Pinnacle-weighted when present. Hydra-computed."
                >
                  <span className="block">Consensus</span>
                  <span className="block text-[9px] font-normal normal-case">(de-vigged)</span>
                </th>
                {books.map((b) => {
                  const at = updatedAt.get(b);
                  const role = b === 'pinnacle' ? 'Pinnacle — the sharp anchor of the consensus. Quoted price, with vig.' : `${oddsBookLabel(b)} — quoted price, with vig.`;
                  return (
                    <th
                      key={b}
                      className="truncate px-1 py-1 text-center"
                      title={at != null ? `${role} Last update ${ageText(at, now)}.` : `${role} No quote for this market.`}
                    >
                      <span className="block truncate">{oddsBookLabel(b)}</span>
                      <span className="block text-[9px] font-normal normal-case tabular-nums" data-book-age={b}>
                        {at != null ? quoteAgeLabel(at, now) : '—'}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody key={marketId}>
              {sides.map((side) => (
                <tr key={side.slot} className="border-border/50 border-b last:border-b-0">
                  <td className="text-foreground truncate px-2 py-1 text-xs font-semibold" title={side.label}>{side.label}</td>
                  <TickCell
                    value={side.consensusTenths}
                    stamp={side.consensusTenths != null ? consensus?.lastUpdateMs ?? null : null}
                    windowMs={OP_FIELD_FRESH_MS}
                    text={fairPctText(side.consensusTenths) ?? '—'}
                    sub={fairAmericanText(side.consensusTenths, null)}
                    emphasis
                    title={consensus != null
                      ? `Consensus of ${consensus.contributingBooks} books${consensus.pinnacleAnchored ? ', Pinnacle-anchored' : ''} — updated ${ageText(consensus.lastUpdateMs, now)}`
                      : 'No consensus for this market'}
                  />
                  {books.map((b) => {
                    const q = side.quotes.get(b);
                    const w = withdrawn.get(b);
                    if (!q) {
                      return (
                        <td
                          key={b}
                          className="text-muted-foreground px-1 py-1 text-center text-xs"
                          title={w ? `${oddsBookLabel(b)} withdrawn — ${opWithdrawReasonLabel(w.reason)}, ${formatOpAge(w.sinceMs, now)}` : `${oddsBookLabel(b)} has no quote for this market`}
                        >
                          {w ? <span className="text-[10px] line-through">{opWithdrawReasonLabel(w.reason)}</span> : '—'}
                        </td>
                      );
                    }
                    const american = americanForQuote(q.quote);
                    const implied = decimalToImpliedPctText(q.quote.decimal);
                    return (
                      <TickCell
                        key={b}
                        value={decimalToImpliedPct(q.quote.decimal)}
                        stamp={q.updatedAtMs}
                        windowMs={b === 'pinnacle' ? OP_ANCHOR_GLOW_MS : OP_FIELD_FRESH_MS}
                        text={implied ?? '—'}
                        sub={american === '—' ? null : american}
                        title={`${oddsBookLabel(b)} ${american}${implied ? ` (${implied}% implied, with vig)` : ''} — updated ${ageText(q.updatedAtMs, now)}`}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </EdgeFadeScroller>
      )}
    </section>
  );
}
